import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import type { IdAdTypeDto } from '@/services/ws-consulting/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Filter } from '@/js/libs/ui/src/V2';

import { useMarkedOptionLabel } from 'src/hooks/useMarkedOptionLabel';
import type { Option } from 'src/types';
import { useConsultingAdTypeOptions } from '../hooks/useConsultingAdTypeOptions';

interface ConsultingAdTypeFilterProps {
  value: Option<IdAdTypeDto> | null;
  onChange: (value: SingleValue<Option<IdAdTypeDto>>) => void;
  label?: string;
}

export const ConsultingAdTypeFilter = React.memo(function ConsultingAdTypeFilter({ value, onChange, label }: ConsultingAdTypeFilterProps) {
  const [t] = useTranslation(['common']);
  const { options, pending: loading, total, searchFilter, setSearchFilter } = useConsultingAdTypeOptions();
  const markedOptionLabel = useMarkedOptionLabel({ t });

  const handleChange = useCallbackRef((newValue: SingleValue<Option<IdAdTypeDto>>) => {
    onChange(newValue);
  });

  return (
    <Filter<IdAdTypeDto>
      loading={loading}
      label={label || t('common:AD_TYPE')}
      selected={value}
      onChange={handleChange}
      serverSideFiltering={{
        filter: searchFilter,
        setFilter: setSearchFilter,
        total,
      }}
      options={options}
      formatOptionLabel={markedOptionLabel}
      dataTestId='ad-type-dropdown'
    />
  );
});
